import { ReactNode } from 'react';
import { useSnapshot } from 'valtio';

import { getDashboardUrl, isNonProduction } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import { authStore, userStore } from 'stores';
import {
  type CanvasEntitlement,
  getCanvasEntitlement,
} from 'utils/canvasEntitlement';

/**
 * Non-production override for previewing gate states without a gated org.
 * localStorage rather than a query param because navigationStore rewrites the
 * URL (dropping the search string) before the first render.
 */
const CANVAS_ENTITLEMENT_OVERRIDE_KEY = 'canvasEntitlementOverride';

function getOverriddenEntitlement(): CanvasEntitlement | undefined {
  if (!isNonProduction()) {
    return undefined;
  }

  let value: string | null = null;
  try {
    value = localStorage.getItem(CANVAS_ENTITLEMENT_OVERRIDE_KEY);
  } catch {
    return undefined;
  }

  return value === 'allowed' || value === 'denied' || value === 'loading'
    ? value
    : undefined;
}

function CanvasUnavailable() {
  return (
    <div className="bg-primary flex h-screen w-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <Text as="h1" variant="studio/title/xl" className="text-primary">
        Canvas isn&apos;t available for your organization
      </Text>
      <Text variant="studio/body/md" className="text-secondary max-w-md">
        Your organization hasn&apos;t enabled Canvas. Contact your administrator
        to request access.
      </Text>
      <a href={getDashboardUrl()}>
        <Button variant="secondary">Go to your dashboard</Button>
      </a>
    </div>
  );
}

/**
 * Blocks every Canvas route, including deep links, when the organization's
 * `allowedApps` whitelist leaves out `canvas`. Rendering the app while the
 * organization is still unknown would flash a half-loaded Canvas at users who
 * are about to be denied, so the gate holds an empty shell until it can decide.
 */
export default function CanvasEntitlementGate({
  children,
}: {
  children: ReactNode;
}) {
  const { authStatus } = useSnapshot(authStore.state);
  const { loading, organizationInfo } = useSnapshot(userStore.state);

  const entitlement =
    getOverriddenEntitlement() ??
    getCanvasEntitlement({
      authStatus,
      userStoreLoading: loading,
      organizationInfo,
    });

  if (entitlement === 'loading') {
    return <div className="bg-primary h-screen w-screen" />;
  }

  if (entitlement === 'denied') {
    return <CanvasUnavailable />;
  }

  return children;
}
